// `AuthLayout` — shared shell for every `/auth/*` page.
//
// DESIGN.md §0.1: 5fr / 7fr split on desktop, 1fr / 1fr on tablet, stacked
// on mobile (96px brand strip + form panel). Locale toggle anchored to the
// top-right of the form panel. Skip-link at the top of `<main>` for SR users.
//
// We deliberately avoid bringing in CSS-in-JS — the grid is composed from
// existing tailwind utility classes already defined by `@barel/ui` token
// CSS, plus a thin set of inline `style` properties for the responsive grid
// that Tailwind doesn't ship out-of-the-box (custom 5fr/7fr ratio).
//
// // DS-GAP: AuthLayout-grid-helper (per DESIGN §5) — local layout primitive.

import type { ReactNode } from 'react';

import { useT } from '@barel/i18n';

import { BrandPanel } from './components/BrandPanel';
import type { BrandPanelProps } from './components/BrandPanel';
import { LocaleToggle } from './components/LocaleToggle';

export interface AuthLayoutProps {
  /** Brand-panel headline variant. Defaults to neutral. */
  brandHeadline?: BrandPanelProps['headlineKey'];
  /** Form-panel content. */
  children: ReactNode;
}

export function AuthLayout({ brandHeadline = 'brandHeadlineNeutral', children }: AuthLayoutProps) {
  const t = useT('auth');

  return (
    <div data-region="auth-layout" className="min-h-[100dvh] bg-bg-base text-primary">
      {/*
        Scoped skip-link — visible only when focused. Targets the same
        `#main-content` landmark used by the global SkipLink in `__root.tsx`
        (US-255) so screen-reader users get a single canonical jump target
        regardless of which copy variant they activate.
      */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-bg-elevated focus:px-3 focus:py-2 focus:text-sm focus:text-primary"
        data-testid="auth-skip-link"
      >
        {t('layout.skipToForm')}
      </a>

      <div
        data-region="auth-grid"
        className="grid min-h-[100dvh] grid-cols-1 md:grid-cols-[5fr_7fr]"
      >
        {/* Brand panel — desktop side, mobile header strip. */}
        <aside data-region="auth-brand-side" aria-hidden="true" className="hidden md:block">
          <BrandPanel headlineKey={brandHeadline} />
        </aside>

        {/* Mobile-only brand strip — 96px tall logo + tagline. */}
        <header
          data-region="auth-brand-mobile"
          aria-hidden="true"
          className="flex h-24 items-center justify-between border-b border-border-default bg-bg-surface px-5 md:hidden"
        >
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-8 w-8 rounded-md"
              style={{ background: 'var(--brand-gradient)' }}
            />
            <span className="text-sm font-semibold text-primary">Barel</span>
          </div>
          <span className="text-xs text-secondary">{t('layout.taglineMobile')}</span>
        </header>

        {/* Form panel. */}
        <section
          data-region="auth-form-panel"
          className="relative flex min-h-[calc(100dvh-6rem)] flex-col px-6 py-8 md:min-h-[100dvh] md:px-12 md:py-16"
        >
          <div className="absolute right-4 top-4 md:right-8 md:top-8">
            <LocaleToggle />
          </div>

          <main
            id="main-content"
            className="mx-auto flex w-full max-w-[440px] flex-1 flex-col justify-center gap-6"
            tabIndex={-1}
          >
            {children}
          </main>

          <footer
            data-region="auth-form-footer"
            className="mx-auto mt-8 flex w-full max-w-[440px] items-center justify-between gap-4 text-xs text-secondary"
          >
            <a
              href="https://barel.app/privacy"
              target="_blank"
              rel="noreferrer noopener"
              className="hover:text-primary"
            >
              {t('layout.privacy')}
            </a>
            <a
              href="https://barel.app/terms"
              target="_blank"
              rel="noreferrer noopener"
              className="hover:text-primary"
            >
              {t('layout.terms')}
            </a>
          </footer>
        </section>
      </div>
    </div>
  );
}
